import { ReactNode } from 'react';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { PnlInfoButton } from '@/components/ledgers/PnlInfoButton';
import type { LedgerType } from '@/api/types';

interface LedgersSummaryCardProps {
    type: LedgerType;
    filteredTotal: number;
    percentageOfTotal: number | null;
    filteredProfitLoss: number | null;
    /** Action element (e.g., Add button or Mode toggle) - always right-aligned */
    action?: ReactNode;
}

/**
 * Summary card showing total value, percentage of portfolio, and P&L.
 * Used by both LedgersListView and LedgersAnalyticsView.
 */
export function LedgersSummaryCard({
    type,
    filteredTotal,
    percentageOfTotal,
    filteredProfitLoss,
    action,
}: LedgersSummaryCardProps) {

    return (
        <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                {/* Total Value - full width on mobile */}
                <div className="min-w-0">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">
                        Total
                    </div>
                    <MoneyDisplay
                        value={filteredTotal}
                        size="lg"
                        className="text-2xl font-bold text-gray-900 dark:text-white"
                    />
                </div>
                {/* Secondary stats + action - shared row */}
                <div className="flex items-center gap-3 sm:gap-4 flex-1">
                    {/* % of Portfolio */}
                    <div className="sm:pl-4 sm:border-l border-gray-300 dark:border-gray-600">
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-0.5">% of Portfolio</div>
                        <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                            {percentageOfTotal !== null ? `${percentageOfTotal.toFixed(1)}%` : '100%'}
                        </div>
                    </div>
                    {/* P&L (for assets - always show to prevent layout jump) */}
                    {type === 'asset' && (
                        <div className="pl-3 sm:pl-4 border-l border-gray-300 dark:border-gray-600">
                            <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-0.5">
                                P&L
                                <PnlInfoButton />
                            </div>
                            {filteredProfitLoss != null ? (
                                <MoneyDisplay value={filteredProfitLoss} size="md" colorize className="text-base sm:text-lg" />
                            ) : (
                                <div className="text-base sm:text-lg font-semibold text-gray-400 dark:text-gray-500">—</div>
                            )}
                        </div>
                    )}
                    {/* Action (Add button / Mode toggle) - always right-aligned and vertically centered */}
                    {action && (
                        <div className="ml-auto shrink-0 self-center">
                            {action}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
